// frontend/src/contexts/AuthContext.js (web)

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth, db } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { toast } from 'sonner';
import {
  hasPremiumAccess as computeHasPremiumAccess,
  isTrialActive,
  isTrialExpired,
  trialCountdownLabel,
  trialDaysRemaining,
} from '../utils/premiumAccess';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null); // 👈 NOUVEAU

  // 🚦 Garde d'inscription — ref et NON state : la valeur doit être lue de façon
  // synchrone par onAuthStateChanged, qui se déclenche AVANT tout re-render React.
  // Un useState serait encore à `false` au moment où le listener s'exécute.
  //
  // Sans cette garde : createUserWithEmailAndPassword déclenche onAuthStateChanged,
  // qui appelle createSession → enforceSessionUniqueness → set(merge) sur users/{uid},
  // ce qui CRÉE le document avec seulement {activeSessionId, lastLoginTime}. Le trigger
  // onUserCreatedGrantTrial se déclenche alors sur ce document incomplet (pas de
  // subscription_type → no-op) et le setDoc de register() ci-dessous n'est plus qu'un
  // update : trial_ends_at n'est jamais posé.
  //
  // Équivalent web de la garde mobile (src/context/AuthContext.js:24-26 et :138).
  const isRegistering = useRef(false);

  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email, {
      url: 'https://remadia.com/reset-password',
      handleCodeInApp: true,
    });
  };

  const resendVerificationEmail = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Aucun utilisateur connecté');
    if (currentUser.emailVerified) throw new Error('Votre email est déjà vérifié');
    await sendEmailVerification(currentUser, {
      url: `${window.location.origin}/auth`,
      handleCodeInApp: false,
    });
    return { success: true };
  };

  //  session unique
  const createSession = async (firebaseUser) => {
    if (!firebaseUser) return;

    // Garde posée ICI et non sur les sites d'appel : onAuthStateChanged appelle
    // createSession depuis TROIS branches (doc existant, doc absent, catch). Un garde
    // par branche en laisserait fatalement passer une.
    if (isRegistering.current) return;

    // Le listener capture `firebaseUser` à l'instant du déclenchement ; entre-temps
    // register() a pu faire signOut(). Créer une session pour un utilisateur qui n'est
    // plus le courant écrirait users/{uid} à contretemps.
    if (auth.currentUser?.uid !== firebaseUser.uid) return;

    try {
      const token = await firebaseUser.getIdToken();

      const response = await fetch(
        'https://europe-west1-medaviceia.cloudfunctions.net/enforceSessionUniqueness',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      const newSessionId = data.sessionId;

      setSessionId(newSessionId);
      localStorage.setItem('sessionId', newSessionId);
    } catch (error) {
      console.error('Erreur création session:', error);
    }
  };

  //  Vérifie validité session
  useEffect(() => {
    if (!user || !sessionId) return;

    const checkSession = async () => {
      try {
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) return;

        const token = await firebaseUser.getIdToken();

        const response = await fetch(
          'https://europe-west1-medaviceia.cloudfunctions.net/checkSessionValidity',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ sessionId }),
          }
        );

        const data = await response.json();

        if (!data.valid) {
          toast.error(
            'Vous avez été déconnecté car une connexion a été établie depuis un autre appareil.'
          );
          await signOut(auth);
          localStorage.removeItem('sessionId');
          setSessionId(null);
        }
      } catch (error) {
        console.error('Erreur vérification session:', error);
      }
    };

    // Vérifier toutes les 30 secondes
    const interval = setInterval(checkSession, 30000);

    return () => clearInterval(interval);
  }, [user, sessionId]);

  /**
   * 🔥 Écoute Firebase
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setRole(null);
        setSessionId(null);
        localStorage.removeItem('sessionId');
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

        if (userDoc.exists()) {
          const userData = userDoc.data();

          const mergedUser = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            emailVerified: firebaseUser.emailVerified,
            first_name: userData.first_name || '',
            last_name: userData.last_name || '',
            name: userData.name || '',
            role: userData.role || 'user',
            subscription_type: userData.subscription_type || 'free',
            // Essai Premium : lu tel quel (Timestamp SDK client ou null). Porté par
            // `user` pour que hasPremiumAccess() ci-dessous puisse trancher sans
            // relecture Firestore dans chaque écran.
            trial_ends_at: userData.trial_ends_at ?? null,
            daily_search_count: userData.daily_search_count ?? 0,
            profile_picture: userData.profile_picture || '',
            avatar: userData.avatar || {},
            birth_year: userData.birth_year,
            profile_type:
              userData.profile_type ||
              (userData.profession === 'medecin'
                ? 'medecin'
                : userData.profession === 'infirmier'
                  ? 'infirmier'
                  : 'non_medical'),
            status: userData.status || null,
            profile_locked: userData.profile_locked || false,
            profile_locked_at: userData.profile_locked_at || null,
            rpps: userData.rpps || '',
            etablissement: userData.etablissement || '',
            specialite: userData.specialite || '',
            createdAt: userData.createdAt || (firebaseUser.metadata?.creationTime ? Timestamp.fromDate(new Date(firebaseUser.metadata.creationTime)) : null),
            updatedAt: userData.updatedAt,
          };

          setUser(mergedUser);
          setRole(userData.role || 'user');

          //  SESSION UNIQUE À CHAQUE CONNEXION
          await createSession(firebaseUser);
        } else {
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            emailVerified: firebaseUser.emailVerified,
            profile_type: 'non_medical',
            profile_locked: false,
            createdAt: firebaseUser.metadata?.creationTime ? Timestamp.fromDate(new Date(firebaseUser.metadata.creationTime)) : null,
          });
          setRole('user');

          // SESSION MÊME POUR NOUVEAUX USERS
          await createSession(firebaseUser);
        }
      } catch (error) {
        console.error('Erreur Firestore:', error);
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          emailVerified: firebaseUser.emailVerified,
          profile_type: 'non_medical',
          profile_locked: false,
        });
        setRole('user');

        await createSession(firebaseUser);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /**
   * 📝 Inscription avec profil VERROUILLÉ
   */
  const register = async (
    email,
    password,
    first_name,
    last_name,
    birth_year,
    profile_type,
    subscription_type = 'free',
    extraData = {}
  ) => {
    try {
      // 0️⃣ Neutraliser createSession pour toute la durée de l'inscription, afin que le
      //    setDoc de l'étape 3 soit bien la CRÉATION du document users/{uid}.
      isRegistering.current = true;

      // 1️⃣ Validation du profil
      if (!profile_type || !['non_medical', 'infirmier', 'medecin'].includes(profile_type)) {
        throw new Error('Profil professionnel invalide ou manquant');
      }

      // 2️⃣ Créer le compte Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      // 3️⃣ Créer le profil Firestore
      await setDoc(doc(db, 'users', uid), {
        email,
        first_name,
        last_name,
        name: `${first_name} ${last_name}`,
        role: 'user',
        subscription_type,
        daily_search_count: 0,
        birth_year,
        profile_type,
        profile_locked: true,
        profile_locked_at: serverTimestamp(),
        emailVerified: false,
        createdAt: serverTimestamp(),
        ...extraData,
      });

      // ✅ EMAIL DE VÉRIFICATION AVEC URL SPÉCIFIQUE
      await sendEmailVerification(userCredential.user, {
        url: 'https://remadia.com/verify-email',
        handleCodeInApp: true,
      });
      // 5️⃣ DÉCONNECTER IMMÉDIATEMENT (forcer vérification email)
      await auth.signOut();

      return userCredential.user;
    } catch (error) {
      console.error('❌ Erreur inscription:', error);

      // 🔥 Rollback : supprimer le compte Auth si Firestore a échoué
      if (auth.currentUser) {
        try {
          await auth.currentUser.delete();
        } catch (deleteError) {
          console.error('❌ Erreur lors du rollback:', deleteError);
        }
      }

      throw error;
    } finally {
      // `finally` s'exécute APRÈS le corps du catch (rollback compris) et avant que
      // l'exception ne se propage : le drapeau est donc toujours levé, succès ou échec.
      isRegistering.current = false;
    }
  };

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await userCredential.user.reload();
      const currentUser = auth.currentUser;

      // Vérifier IMMÉDIATEMENT avant que onAuthStateChanged charge quoi que ce soit
      if (!currentUser.emailVerified) {
        // Vérifier si admin
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const isAdmin = userDoc.exists() && userDoc.data().role === 'admin';

        if (!isAdmin) {
          // DÉCONNECTER IMMÉDIATEMENT
          await auth.signOut();

          // Créer une erreur custom avec flag
          const error = new Error('EMAIL_NOT_VERIFIED');
          error.isEmailNotVerified = true; // 👈 FLAG SPÉCIAL
          throw error;
        }
      }

      return currentUser;
    } catch (error) {
      // Si c'est notre erreur custom, la passer telle quelle
      if (error.isEmailNotVerified) {
        throw error;
      }

      // Sinon, déconnecter en cas d'erreur
      if (auth.currentUser) {
        await auth.signOut();
      }

      console.error('Erreur connexion Firebase:', error);
      throw error;
    }
  };

  /**
   * 🚪 Déconnexion
   */
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setRole(null);
      setSessionId(null);
      localStorage.removeItem('sessionId');
    } catch (error) {
      console.error('Erreur déconnexion Firebase:', error);
      throw error;
    }
  };

  /**
   * 👤 Profil utilisateur
   */
  const getUserProfile = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      return userDoc.exists() ? userDoc.data() : null;
    } catch (error) {
      console.error('Erreur récupération profil:', error);
      throw error;
    }
  };

  /**
   * 🔄 Mise à jour utilisateur
   */
  const updateUser = (updatedData) => {
    if (user?.profile_locked && updatedData.profile_type) {
      console.warn('🔒 Tentative de modification du profil verrouillé bloquée');
      delete updatedData.profile_type;
    }

    setUser((prev) => ({ ...prev, ...updatedData }));
  };

  // ⚠️ `user` est chargé par un getDoc ONE-SHOT dans onAuthStateChanged (pas un
  // onSnapshot comme sur mobile) : la bascule fin d'essai → free n'est visible qu'au
  // rechargement de la page ou à la reconnexion. Acceptable, car la frontière réelle
  // est côté serveur (les gates recalculent hasPremiumAccess à chaque appel) : au pire
  // l'utilisateur voit un écran accessible et reçoit un refus du backend.
  const hasPremiumAccess = computeHasPremiumAccess(user);
  const isTrial = isTrialActive(user);
  const trialExpired = isTrialExpired(user);
  const trialDaysLeft = trialDaysRemaining(user);
  const trialLabel = trialCountdownLabel(trialDaysLeft);

  const value = {
    user,
    currentUser: user,
    role,
    loading,
    sessionId,
    hasPremiumAccess, // accès premium = abonnement OU essai en cours
    isTrial, // en essai et pas abonné payant — pour les bandeaux
    isTrialExpired: trialExpired, // essai CONSOMMÉ (≠ jamais eu) — pour les messages de blocage
    trialDaysLeft, // jours entiers restants, 0 hors essai
    trialCountdownLabel: trialLabel, // « J-3 » ou « Dernier jour » — à afficher tel quel
    register,
    login,
    logout,
    getUserProfile,
    updateUser,
    resetPassword,
    resendVerificationEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
